import { useEffect, useState } from "react";
import { api, fmtDateTime, ROLE_LABEL } from "@/lib/api";
import { Plus } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

const EMPTY = { name: "", email: "", roles: ["sales"], phone: "", status: "active", password: "" };

export default function Users() {
  const { user, hasRole } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/users").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const startEdit = (u) => { setEditing(u); setForm({ ...EMPTY, ...u, password: "" }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/users", form);
        toast.success("User created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  const deactivate = async (u) => {
    if (!hasRole("super_admin")) return;
    if (!window.confirm(`Deactivate ${u.email}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Deactivated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const deleteUser = async (u) => {
    if (!hasRole("super_admin")) return;
    if (!window.confirm(`Permanently delete ${u.email}? This cannot be undone.`)) return;
    try { await api.delete(`/users/${u.id}/permanent`); toast.success("User deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const isSuperAdmin = user?.roles?.includes("super_admin");
  const roleOptions = isSuperAdmin
    ? ["super_admin", "admin", "accounts", "operations", "sales"]
    : ["admin", "accounts", "operations", "sales"];

  return (
    <div className="space-y-5" data-testid="users-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-mini">Access Control</div>
          <h1 className="text-3xl font-bold tracking-tight mt-1">Users &amp; Roles</h1>
        </div>
        <Button onClick={startNew} className="h-9 rounded-sm bg-black text-white hover:bg-zinc-800" data-testid="add-user-btn">
          <Plus size={14} className="mr-1" /> New User
        </Button>
      </div>

      <div className="card-sharp overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50">
            <tr className="text-left">
              <th className="label-mini px-4 py-3">Name</th>
              <th className="label-mini px-4 py-3">Email</th>
              <th className="label-mini px-4 py-3">Roles</th>
              <th className="label-mini px-4 py-3">Phone</th>
              <th className="label-mini px-4 py-3">Status</th>
              <th className="label-mini px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.id} className="border-t border-zinc-100" data-testid={`user-row-${u.email}`}>
                <td className="px-4 py-3 font-medium">{u.name}</td>
                <td className="px-4 py-3 text-xs font-mono">{u.email}</td>
                <td className="px-4 py-3 space-x-1">
                  {(u.roles || []).map((r) => (
                    <span key={r} className="status-badge" style={{ background: "#0A0A0A", color: "white", borderColor: "#0A0A0A" }}>{ROLE_LABEL[r]}</span>
                  ))}
                </td>
                <td className="px-4 py-3 text-xs">{u.phone || "—"}</td>
                <td className="px-4 py-3">
                  <span className="status-badge" style={{
                    background: u.status === "active" ? "#D1FAE5" : "#F4F4F5",
                    color: u.status === "active" ? "#065F46" : "#52525B",
                    borderColor: u.status === "active" ? "#10B981" : "#E4E4E7",
                  }}>{u.status}</span>
                </td>
                <td className="px-4 py-3 text-right space-x-3">
                  {(isSuperAdmin || !u.roles?.includes("super_admin")) && (
                    <button onClick={() => startEdit(u)} className="text-xs font-semibold underline" data-testid={`edit-user-${u.email}`}>Edit</button>
                  )}
                  {isSuperAdmin && u.id !== user.id && (
                    <>
                      <button onClick={() => deactivate(u)} className="text-xs font-semibold underline text-red-700">Deactivate</button>
                      <button onClick={() => deleteUser(u)} className="text-xs font-semibold underline text-red-700" data-testid={`delete-user-${u.email}`}>Delete</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && (<tr><td colSpan={6} className="py-8 text-center text-xs text-zinc-500">No users.</td></tr>)}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="user-form-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit User" : "New User"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name" required>
              <Input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm h-9" />
            </Field>
            <Field label="Email" required>
              <Input data-testid="user-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!editing} className="rounded-sm h-9 font-mono" />
            </Field>
            <Field label="Roles" required>
              <div className="grid grid-cols-2 gap-2 mt-1" data-testid="user-roles">
                {roleOptions.map((r) => (
                  <label key={r} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={form.roles?.includes(r)}
                      onCheckedChange={(checked) => {
                        const roles = checked
                          ? [...(form.roles || []), r]
                          : (form.roles || []).filter((x) => x !== r);
                        setForm({ ...form, roles });
                      }}
                    />
                    {ROLE_LABEL[r]}
                  </label>
                ))}
              </div>
            </Field>
            <Field label="Phone">
              <Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-sm h-9" />
            </Field>
            <Field label="Status">
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="rounded-sm h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label={editing ? "New Password (leave blank)" : "Password"} required={!editing}>
              <Input data-testid="user-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-sm h-9 font-mono" />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={save} disabled={saving || !form.name || !form.email || !form.roles?.length || (!editing && !form.password)} className="rounded-sm bg-black text-white hover:bg-zinc-800" data-testid="save-user-btn">
              {saving ? "Saving…" : editing ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, required, children }) => (
  <div>
    <label className="label-mini">{label}{required && <span className="text-red-600">*</span>}</label>
    <div className="mt-1">{children}</div>
  </div>
);
